import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Playing the prompt (FR-067, FR-068, SC-017, research D-006, D-010).
 *
 * Four rules this hook exists to hold:
 *
 *  1. REPLAY IS UNLIMITED AND UNCOUNTED. There is deliberately no counter and
 *     no cap in this file — not a hidden one, not a logged one. A replay
 *     changes no recorded field (FR-067).
 *  2. PLAYBACK FAILURE BLOCKS ANSWERING. Blocked autoplay, a decode error and
 *     a failed fetch are all detectable; the hook reports `error`, and the
 *     caller disables the options, so no attempt can be submitted for a note
 *     the learner never heard (FR-068). The pending row simply never receives
 *     a `submitted_at` — nothing needs undoing.
 *  3. THE FIRST PLAY NEEDS A GESTURE. Browsers require one, so the control is
 *     an explicit play affordance rather than an autoplay the hook retries.
 *  4. THE NEXT SAMPLE IS PREFETCHED during the feedback window (research
 *     D-010), which is the dead time the learner already spends reading the
 *     result, and is how the 300 ms p95 of SC-020 is met.
 */

export type AudioState = 'idle' | 'loading' | 'playing' | 'played' | 'error';

export interface AudioController {
  readonly state: AudioState;
  /** Play or replay. Safe to call at any time and any number of times. */
  readonly play: () => void;
  /** Retry after a failure. Same action as `play`, named for the error state. */
  readonly retry: () => void;
  /** True once the note has been heard at least once — the answering gate. */
  readonly heard: boolean;
  /** Warms the browser cache for the sample the next Exercise will play. */
  readonly prefetch: (url: string) => void;
}

/**
 * `url` is the content-hashed sample for the current attempt, or null while
 * there is nothing to play. Changing it resets the controller: a new Exercise
 * has not been heard yet, whatever the previous one did.
 */
export function useAudio(url: string | null): AudioController {
  const [state, setState] = useState<AudioState>('idle');
  const [heard, setHeard] = useState(false);
  const elementRef = useRef<HTMLAudioElement | null>(null);
  const prefetched = useRef(new Map<string, HTMLAudioElement>());

  useEffect(() => {
    setState('idle');
    setHeard(false);

    if (!url) {
      elementRef.current = null;
      return;
    }

    // A sample prefetched during the previous feedback window is already
    // decoded; reusing that element is the whole point of the prefetch.
    const existing = prefetched.current.get(url);
    const element = existing ?? new Audio(url);
    element.preload = 'auto';
    elementRef.current = element;
    prefetched.current.delete(url);

    // A fresh element starts fetching HERE, at mount, rather than at the
    // learner's first gesture. `preload` set after construction is only a
    // hint the browser may already have passed, so the fetch is asked for
    // explicitly: it is the difference between the sample downloading during
    // the seconds the learner spends looking at a new Exercise and it
    // downloading after they press play, which is the whole of FR-076's
    // 300 ms budget. An element handed over already prefetched is left alone
    // — calling load() on it would discard the buffer that is the point of it.
    if (!existing) element.load();

    return () => {
      element.pause();
      elementRef.current = null;
    };
  }, [url]);

  const play = useCallback(() => {
    const element = elementRef.current;
    if (!element) return;

    setState('loading');
    element.currentTime = 0;

    const onEnded = (): void => {
      setState('played');
      element.removeEventListener('ended', onEnded);
    };
    element.addEventListener('ended', onEnded);

    void element
      .play()
      .then(() => {
        setState('playing');
        setHeard(true);
      })
      .catch(() => {
        // Blocked autoplay, a decode failure, or a missing asset all land
        // here. The state is explicit and the caller disables the options; it
        // is never swallowed and never retried silently.
        element.removeEventListener('ended', onEnded);
        setState('error');
      });
  }, []);

  const prefetch = useCallback((next: string) => {
    if (!next || prefetched.current.has(next)) return;
    const element = new Audio(next);
    element.preload = 'auto';
    // Kept only until it is consumed or the component unmounts. A handful of
    // decoded two-second samples is not a cache worth managing.
    prefetched.current.set(next, element);
    element.load();
  }, []);

  useEffect(() => {
    const cache = prefetched.current;
    return () => {
      for (const element of cache.values()) element.pause();
      cache.clear();
    };
  }, []);

  return { state, play, retry: play, heard, prefetch };
}
